import React from 'react'
import {motion} from 'framer-motion'
import {Link} from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiArrowLeft} = FiIcons

const TermsOfService = () => {
  const lastUpdated = "January 15, 2024"

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700 mb-8 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-6">
                By accessing and using Empower Nutrition's platform, courses, and services ("Services"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                Empower Nutrition provides online nutrition education courses, coaching materials, and related content. Our services include:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Online courses covering nutrition science and practical application</li>
                <li>Educational videos, written content, and downloadable resources</li>
                <li>Progress tracking and completion certificates</li>
                <li>Interactive quizzes and assessments</li>
                <li>Community features and support</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Account Creation</h3>
              <p className="text-gray-700 mb-4">
                To access certain features, you must create an account by providing accurate and complete information. You are responsible for:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Keeping your account information current and accurate</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Account Termination</h3>
              <p className="text-gray-700 mb-6">
                We reserve the right to terminate or suspend accounts that violate these terms, engage in fraudulent activity, or misuse our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Course Access and Enrollment</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Course Purchases</h3>
              <p className="text-gray-700 mb-4">
                When you purchase a course:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>You receive a non-exclusive, non-transferable license to access the content</li>
                <li>Access is granted for personal, non-commercial use only</li>
                <li>You may not share, distribute, or resell course content</li>
                <li>Course access is typically lifetime unless otherwise specified</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Refund Policy</h3>
              <p className="text-gray-700 mb-6">
                We offer a 30-day money-back guarantee for course purchases. To request a refund, contact us at laura@empowernutrition.info within 30 days of purchase. Refunds are processed to the original payment method within 5-10 business days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Content</h3>
              <p className="text-gray-700 mb-4">
                All course materials, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Videos, audio recordings, and written content</li>
                <li>Images, graphics, and visual materials</li>
                <li>Quizzes, assessments, and interactive elements</li>
                <li>Software, code, and platform functionality</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Are protected by copyright and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our explicit written permission.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">User-Generated Content</h3>
              <p className="text-gray-700 mb-6">
                By submitting content (notes, comments, feedback), you grant us a non-exclusive, worldwide, royalty-free license to use, modify, and display such content for the purpose of providing and improving our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">
                You agree not to use our services to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Share login credentials or course access with others</li>
                <li>Attempt to reverse engineer or hack our platform</li>
                <li>Upload malicious software or engage in harmful activities</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Use our services for commercial purposes without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Medical Disclaimer</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 font-semibold mb-2">Important Medical Disclaimer</p>
                <p className="text-yellow-800 text-sm">
                  The information provided in our courses is for educational purposes only and is not intended as medical advice, diagnosis, or treatment. Always consult with qualified healthcare professionals before making changes to your diet or health regimen.
                </p>
              </div>
              
              <p className="text-gray-700 mb-4">
                Our courses and content:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Are not intended to replace professional medical advice</li>
                <li>Should not be used to diagnose or treat medical conditions</li>
                <li>Are based on general nutrition principles that may not apply to everyone</li>
                <li>Should be adapted to your individual needs and circumstances</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                To the fullest extent permitted by law:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>We provide our services "as is" without warranties of any kind</li>
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our total liability is limited to the amount you paid for our services</li>
                <li>We do not guarantee specific outcomes from using our courses</li>
                <li>You assume full responsibility for your use of our information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Privacy</h2>
              <p className="text-gray-700 mb-6">
                Your privacy is important to us. Please review our <Link to="/privacy" className="text-nutrition-600 hover:text-nutrition-700">Privacy Policy</Link> to understand how we collect, use, and protect your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Payment Terms</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Pricing</h3>
              <p className="text-gray-700 mb-4">
                Course prices are displayed in USD and may change without notice. Current pricing applies at the time of purchase.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Payment Processing</h3>
              <p className="text-gray-700 mb-4">
                Payments are processed securely through Stripe. We do not store your payment information on our servers.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Taxes</h3>
              <p className="text-gray-700 mb-6">
                You are responsible for any applicable taxes based on your location and local laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Modifications to Terms</h2>
              <p className="text-gray-700 mb-6">
                We reserve the right to modify these terms at any time. Material changes will be communicated via email or platform notification. Continued use of our services after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700 mb-6">
                These terms are governed by the laws of the United States and the state in which our business is registered. Any disputes will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Severability</h2>
              <p className="text-gray-700 mb-6">
                If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about these terms or need to contact us for any reason:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:laura@empowernutrition.info" className="text-nutrition-600 hover:text-nutrition-700">laura@empowernutrition.info</a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Business Name:</strong> Empower Nutrition
                </p>
                <p className="text-gray-700">
                  <strong>Response Time:</strong> We typically respond within 48 hours
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default TermsOfService